import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes/platform.class';
import { ConfirmationDialogComponent, DeprecatedModalComponent, DeprecatedModalFormComponent } from '@app/components';
import { CalculateBalanceForm } from '@app/components/calculate-balance/calculate-balance.form';
import {
    DisplayDateSetting,
    ProfileBalanceDisplayServiceProvider,
    TimeOffProfileBalanceDisplayService,
} from '@app/components/time-off-policy-card/time-off-profile-balance-display.service';
import { TimeOffTypeDetails } from '@app/components/time-off-type-details/time-off-type-details.component';
import { AnalyticEvents } from '@app/enums';
import { LegendItem } from '@app/interfaces/legend-item.interface';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffTransaction } from '@app/models/time-off-v3/time-off-transaction.model';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { TypeAndPolicyWithBalance } from '@app/modules/time-off-v3/interfaces/time-off-balance.interface';
import { TimeOffDateFormatDashes } from '@app/modules/time-off-v3/meta/time-off-meta';
import { TimeOffRequestTable, TimeOffService } from '@app/modules/time-off-v3/services';
import { TimeOffTransactionTable } from '@app/modules/time-off-v3/services/time-off-transaction-table/time-off-transaction.table';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { Translatable } from '@app/types/translatable.type';
import { Employee } from '@models/employee/employee.model';
import { TimeOffType } from '@models/time-off-v3/time-off-type.model';
import { TimeOffRequestCalendarEvent } from '@models/time-off/time-off-request-calendar-event.model';
import { AdjustmentForm } from '@time-off-v3/forms/adjustment/adjustment.form';
import { ChangePolicyForm } from '@time-off-v3/forms/change-policy/change-policy.form';
import { setTime } from '@time-off-v3/functions/time-unit-converter';
import { uniqBy } from 'lodash-es';
import moment from 'moment';

@Component({
    templateUrl: './time-off-show-v3.template.html',
    styleUrls: ['./time-off-show-v3.style.scss'],
    providers: [ProfileBalanceDisplayServiceProvider],
})
export class TimeOffShowViewV3 implements OnInit {
    @ViewChild('adjustmentForm') adjustmentForm: AdjustmentForm;
    @ViewChild('detailModal') detailModal: DeprecatedModalComponent;
    @ViewChild('timeOffTypeDetails') timeOffTypeDetails: TimeOffTypeDetails;
    @ViewChild('changePolicyModal') changePolicyModal: DeprecatedModalFormComponent;
    @ViewChild('changePolicyForm') changePolicyForm: ChangePolicyForm;
    @ViewChild('calculateBalanceModal') calculateBalanceModal: DeprecatedModalFormComponent;
    @ViewChild('calculateBalanceForm') calculateBalanceForm: CalculateBalanceForm;
    @ViewChild('adjustmentModal') adjustmentModal: DeprecatedModalFormComponent;
    @ViewChild('deleteTransactionModal') deleteTransactionModal: ConfirmationDialogComponent;

    eventTypes = [TimeOffRequestCalendarEvent];
    isLoading = true;
    endDate: string;

    employee: Employee;
    transactionTable: TimeOffTransactionTable;
    timeOffTypeAndPolicyWithBalances: TypeAndPolicyWithBalance[];
    requestTable: TimeOffRequestTable;
    timeOffPolicy: TimeOffPolicy;
    timeOffTypes: TimeOffType[];

    selectedTimeOffTypeAndPolicyWithBalance: TypeAndPolicyWithBalance;

    calendarLegend: LegendItem[] = [];
    calendarPermission: string[] = Platform.timeOff.permission.calendar;

    viewerIsAdmin = false;
    viewerIsManager = false;
    hasTypesAssigned = false;
    modalHeight = '700px';
    balanceDateSetting: string = DisplayDateSetting.TODAY;
    showEraseAccrualConfirmation = false;
    showCalculateBalanceModal = false;

    isTimeOffTypeDetailsOpen = false;

    constructor(
        private route: ActivatedRoute,
        private auth: AuthService,
        private timeOffService: TimeOffService,
        private notify: NotifyService,
        private router: Router,
        private analyticService: AnalyticService,
        private profileBalanceDisplayService: TimeOffProfileBalanceDisplayService
    ) {
        this.employee = this.route.snapshot.data.models.get('employee');
    }

    async ngOnInit(): Promise<void> {
        await this.loadTimeOffTypeAndPolicyWithBalances();

        const allAssignedTimeOffPolicies = this.employee.allAssignedTimeOffPolicies;

        this.timeOffTypes = allAssignedTimeOffPolicies.map((policy) => {
            policy.timeOffRequestRule.timeOffType = policy.timeOffType;

            return policy.timeOffType;
        });
        // Remove any duplicate time off types which can occur from a future time off policy being assigned to
        this.timeOffTypes = uniqBy(this.timeOffTypes, (timeOffType: TimeOffType) => timeOffType.id);

        // If there is no time off types/policies assigned to the employee we stop here and show empty state
        if (!this.timeOffTypes.length) {
            return;
        }

        this.hasTypesAssigned = true;

        this.endDate = moment().format(TimeOffDateFormatDashes);

        this.requestTable = new TimeOffRequestTable(this.auth, this.timeOffTypes, this.employee.id, {
            includeTerminated: true,
        });

        this.instantiateAndAssignTimeOffTransactionsTable();

        this.viewerIsAdmin = this.auth.isAdmin();
        this.viewerIsManager = this.auth.supervises(this.employee);
    }

    getUnitOfTime(timeOffType: TimeOffType): Translatable {
        return timeOffType.displayInHours ? 'time-off-v3.hours' : 'time-off-v3.days';
    }

    async loadTimeOffTypeAndPolicyWithBalances(): Promise<void> {
        try {
            this.isLoading = true;

            this.calendarLegend = this.employee.activeTimeOffPolicies.map((timeOffPolicy) => {
                return {
                    title: timeOffPolicy.timeOffType.name,
                    color: timeOffPolicy.timeOffType.color,
                };
            });

            const timeOffTypeAndPolicyCombined: TypeAndPolicyWithBalance[] = await this.combineTimeOffTypeAndPolicy();

            this.balanceDateSetting = await this.profileBalanceDisplayService.balanceDateSetting;

            this.timeOffTypeAndPolicyWithBalances = timeOffTypeAndPolicyCombined;
            this.isLoading = false;
        } catch (e) {
            this.notify.error(e);
        }
    }

    async combineTimeOffTypeAndPolicy(): Promise<TypeAndPolicyWithBalance[]> {
        // We need to access a different property for future dated assignment which is inclusive of the data of "timeOffPolicies" property

        const markedMultiPolicies = this.employee.allAssignedTimeOffPolicies.map((timeOffPolicy: TimeOffPolicy) => {
            timeOffPolicy.isAMultiPolicy = this.doesTimeOffPolicyExistWithSameTimeOffType(
                timeOffPolicy,
                this.employee.allAssignedTimeOffPolicies
            );

            return timeOffPolicy;
        });

        // Get all policies that are in the future and do not belong to a currently active policy and it's time off type
        const futureTimeOffPoliciesWithNoMultiPolicies = markedMultiPolicies.filter(
            (timeOffPolicy: TimeOffPolicy) => timeOffPolicy.isFuture && !timeOffPolicy.isAMultiPolicy
        );

        // All active time off policies currently
        const currentTimeOffPolicies = markedMultiPolicies.filter(
            (timeOffPolicy: TimeOffPolicy) => !timeOffPolicy.isFuture
        );

        // For current active policies attach the next future time off policy for each respective time off type
        currentTimeOffPolicies.forEach((timeOffPolicy: TimeOffPolicy) => {
            timeOffPolicy.nextFuturePolicy = this.findNextFutureTimeOffPolicy(timeOffPolicy);
        });

        const allAssignedTimeOffPolicies = [...currentTimeOffPolicies, ...futureTimeOffPoliciesWithNoMultiPolicies];

        return await Promise.all(
            allAssignedTimeOffPolicies.map(async (timeOffPolicy: TimeOffPolicy) => {
                const timeOffBalance = await this.timeOffService.getBalance(this.employee.id, {
                    'filters[timeOffTypeId]': timeOffPolicy.timeOffType.id,
                    'filters[processedAt][to]': await this.profileBalanceDisplayService.balanceDateFor(timeOffPolicy),
                });

                timeOffBalance.balanceDateLabel =
                    await this.profileBalanceDisplayService.balanceDateLabelFor(timeOffPolicy);

                return { timeOffType: timeOffPolicy.timeOffType, timeOffBalance, timeOffPolicy };
            })
        );
    }
    /**
     * Finds the closest time off policy in the future for display purposes
     */
    findNextFutureTimeOffPolicy(currentTimeOffPolicy: TimeOffPolicy): TimeOffPolicy {
        let nextClosestTimeOffPolicy: TimeOffPolicy;

        // This is to get all of the future policies for the current time
        const futureTimeOffPolicies = this.employee.allAssignedTimeOffPolicies.filter(
            (timeOffPolicy: TimeOffPolicy) =>
                currentTimeOffPolicy.id !== timeOffPolicy.id &&
                timeOffPolicy.timeOffTypeId === currentTimeOffPolicy.timeOffTypeId
        );

        futureTimeOffPolicies.forEach((timeOffPolicy: TimeOffPolicy) => {
            if (nextClosestTimeOffPolicy) {
                // Get the difference of days for each policy to the current active policy to determine which one is next in line.
                const nextClosestTimeOffPolicyDiff = moment(nextClosestTimeOffPolicy.effectiveAt).diff(
                    moment(currentTimeOffPolicy.effectiveAt),
                    'days'
                );

                const timeOffPolicyDiff = moment(timeOffPolicy.effectiveAt).diff(
                    moment(currentTimeOffPolicy.effectiveAt),
                    'days'
                );

                if (timeOffPolicyDiff < nextClosestTimeOffPolicyDiff) {
                    nextClosestTimeOffPolicy = timeOffPolicy;
                }
            }
            // Set the the closest policy the first time
            if (!nextClosestTimeOffPolicy) {
                nextClosestTimeOffPolicy = timeOffPolicy;
            }
        });

        return nextClosestTimeOffPolicy;
    }

    doesTimeOffPolicyExistWithSameTimeOffType(
        currentTimeOffPolicy: TimeOffPolicy,
        allAssignedTimeOffPolicies: TimeOffPolicy[]
    ): boolean {
        return allAssignedTimeOffPolicies.some((timeOffPolicy: TimeOffPolicy) => {
            return (
                timeOffPolicy.timeOffTypeId === currentTimeOffPolicy.timeOffTypeId &&
                timeOffPolicy.id !== currentTimeOffPolicy.id
            );
        });
    }

    async viewDetails(timeOffTypeAndPolicyWithBalance: TypeAndPolicyWithBalance): Promise<void> {
        try {
            this.isLoading = true;

            /*
             * If the date setting is "Today" we already have today's balance.
             * Otherwise, we need to calculate today's balance
             * for the details dialoge.
             */
            this.selectedTimeOffTypeAndPolicyWithBalance =
                this.balanceDateSetting === DisplayDateSetting.TODAY
                    ? timeOffTypeAndPolicyWithBalance
                    : await this.getTodayBalance(timeOffTypeAndPolicyWithBalance.timeOffPolicy);

            await this.timeOffTypeDetails.loadData(timeOffTypeAndPolicyWithBalance);

            this.isTimeOffTypeDetailsOpen = true;
            this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTimeOffPolicyCardMeatballViewDetailsDone);

            this.isLoading = false;
        } catch (err) {
            this.isLoading = false;
            this.notify.error(err);
        }
    }

    async getTodayBalance(timeOffPolicy: TimeOffPolicy): Promise<TypeAndPolicyWithBalance> {
        const timeOffBalance = await this.timeOffService.getBalance(this.employee.id, {
            'filters[timeOffTypeId]': timeOffPolicy.timeOffTypeId,
        });

        return { timeOffType: timeOffPolicy.timeOffType, timeOffBalance, timeOffPolicy };
    }

    viewAdjustment(timeOffTypeAndBalance: TypeAndPolicyWithBalance): void {
        // Reset the time off transaction since the app modal will not reinitialize.
        this.adjustmentForm.timeOffTransaction = new TimeOffTransaction({
            processedAt: moment().format(TimeOffDateFormatDashes),
            isBalanceReset: false,
        });
        this.adjustmentForm.timeOffTransaction.employeeId = this.employee.id;
        this.adjustmentForm.timeOffTransaction.timeOffTypeId = timeOffTypeAndBalance.timeOffType.id;
        this.adjustmentModal.show();
    }

    viewCalculateBalanceModal(timeOffTypeId: number, carryoverCap: number): void {
        this.calculateBalanceForm.timeOffTypeId = timeOffTypeId;
        this.calculateBalanceForm.carryoverCap = carryoverCap;
        this.calculateBalanceForm.getBalanceOnDateSelect();
        this.showCalculateBalanceModal = true;
        this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTimeOffPolicyCardMeatballCalculateBalanceDone);
    }

    onClose(): void {
        this.showCalculateBalanceModal = false;
    }

    onSaveAdjustment(): void {
        const transaction = this.adjustmentForm.timeOffTransaction;
        const timeOffType = this.timeOffTypes.find((type) => type.id === transaction.timeOffTypeId);

        transaction.amount = setTime(transaction.amount, timeOffType);

        transaction
            .save()
            .then(() => {
                this.loadTimeOffTypeAndPolicyWithBalances();
                this.transactionTable.reload();
                this.requestTable.reload();
                this.notify.success('time-off-v3.adjustmentCreated');
                this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTimeOffPolicyCardMeatballMakeAdjustment);
            })
            .catch((err) => {
                this.notify.error(err);
            });
    }

    async viewChangePolicy(timeOffTypeId: number): Promise<void> {
        this.isLoading = true;
        await this.changePolicyForm.getTimeOffPolicies(timeOffTypeId, this.employee.timeOffPolicies);
        this.changePolicyModal.show();
        this.isLoading = false;
    }

    onSaveChangePolicy(changePolicyForm: ChangePolicyForm): void {
        this.isLoading = true;

        // Update the current employee's policy
        this.timeOffService
            .assignAndUnassignEmployeesToPolicy(changePolicyForm.selectedTimeOffPolicyId, {
                employees: [
                    {
                        effectiveAt: changePolicyForm.selectedDate.toDate(),
                        id: this.employee.id,
                    },
                ],
            })
            .then(() => {
                this.isLoading = false;
                this.notify.success('time-off-v3.policyHasBeenUpdated');
                this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTimeOffPolicyCardMeatballChangePolicy);
            })
            .catch(() => {
                this.isLoading = false;
                this.notify.error('time-off-v3.thisEmployeeIsCurrentlyAssignedToThisPolicy');
            });
    }

    onClick(): void {
        this.router.navigate(['/', 'time-off', 'v3', 'requests', 'create'], {
            queryParams: { employeeId: this.employee.id },
        });
    }

    getBalanceAmount(typeAndPolicyWithBalance: TypeAndPolicyWithBalance): number {
        const { timeOffType, timeOffBalance } = typeAndPolicyWithBalance;

        return timeOffType.displayInHours ? timeOffBalance.balanceHours : timeOffBalance.balance;
    }

    async onMenuAction(event: MenuColumnEvent): Promise<void> {
        if (event.menuItem !== TimeOffTransactionTable.deleteOption) {
            return;
        }

        const approved = await this.deleteTransactionModal.open();

        if (!approved) {
            return;
        }

        try {
            await (event.data as TimeOffTransaction).delete();
            this.notify.success('time-off-v3.transactionDeletedSuccessfully');
            this.loadTimeOffTypeAndPolicyWithBalances();
            this.transactionTable.reload();
        } catch (e) {
            this.notify.error(e);
        }
    }

    getTransactionTableEmptyStateLabel(): Translatable {
        return this.transactionTable.isPolicyUnlimited
            ? 'time-off-v3.noTransactionsFoundForUnlimitedPolicies'
            : 'time-off-v3.noTransactionsFound';
    }

    showEraseAccrualsDialog(selectedTimeOffTypeAndBalance: TypeAndPolicyWithBalance): void {
        this.selectedTimeOffTypeAndPolicyWithBalance = selectedTimeOffTypeAndBalance;
        this.showEraseAccrualConfirmation = true;
    }

    async eraseAccruals(): Promise<void> {
        this.showEraseAccrualConfirmation = false;

        this.isLoading = true;

        try {
            await this.timeOffService.eraseAllAccruals(
                this.employee.id,
                this.selectedTimeOffTypeAndPolicyWithBalance.timeOffType.id
            );
            await this.reloadActivePoliciesAndTypes();
            await this.loadTimeOffTypeAndPolicyWithBalances();
            this.instantiateAndAssignTimeOffTransactionsTable();
            this.requestTable.reload();
            this.notify.success('time-off-v3.accrualsErased');
            this.analyticService.trackEvent(AnalyticEvents.EmployeeProfileTimeOffPolicyCardMeatballEraseAccruals);
        } catch (e) {
            this.notify.error('time-off-v3.error');
        }

        this.isLoading = false;
    }

    canCalculateBalance(timeOffTypeAndPolicyWithBalance: TypeAndPolicyWithBalance): boolean {
        return (
            (this.viewerIsAdmin || this.viewerIsManager) &&
            !timeOffTypeAndPolicyWithBalance.timeOffPolicy.isUnlimited &&
            !timeOffTypeAndPolicyWithBalance.timeOffPolicy?.isFuture
        );
    }

    private async reloadActivePoliciesAndTypes(): Promise<void> {
        this.employee = await Employee.with([
            'activeTimeOffPolicies',
            'activeTimeOffPolicies.timeOffType',
            'activeTimeOffPolicies.timeOffRequestRule',
        ])
            .param('company', this.employee.companyId)
            .find(this.employee.id);
        this.timeOffTypes = this.employee.activeTimeOffPolicies.map((policy) => {
            policy.timeOffRequestRule.timeOffType = policy.timeOffType;

            return policy.timeOffType;
        });
    }

    private instantiateAndAssignTimeOffTransactionsTable(): void {
        this.transactionTable = new TimeOffTransactionTable(
            this.auth,
            this.employee.id,
            this.endDate,
            this.timeOffTypes,
            this.employee.activeTimeOffPolicies
        );
    }
}
